import { capitalize, isEmpty, isNull } from 'lodash';
import { DROPDOWN_TYPE_LABEL, TEXT_TYPE_LABEL } from 'common/constants/firms';
import { FirmCreateDocRequestPayload, FirmCreateQuestionRequestPayload, Option } from 'common/types/firm';
import {
  createIndividualDocument,
  createIndividualQuestion,
  createQuestions,
  createRequestedDocuments,
  retrieveUpdateDestroyCompanyQuestion,
  retrieveUpdateDestroyCompanyRequestedDocument,
  updateDestroyIndividualQuestion,
  updateIndividualDocument,
} from 'dashboard409a/services/dashboard';
import {
  getQuestionTypeLabel,
  getQuestionTypeValue,
  resolveQuestionTypeValue,
} from './questionTypeUtilities';
import {
  DashboardCreateDocRequestPayload,
  DashboardCreateQuestionRequestPayload,
  PayloadToUpdateRequestedQuestion,
  SubQuestion,
  UseRequestDocsAndQuestionsProps,
} from './types';
import type { UpdateIndividualQuestion } from '../../../api';
import {
  useCreateFirmRequestedDocument,
  useCreateRequestedQuestion,
  useDeleteAndUpdateFirmRequestedDocument,
  useDeleteAndUpdateRequestedQuestion,
} from '../firm';

const toOptionNames = (options: Option[]) => options.map(option => option.name ?? '');

const toResolvedOptionNames = (
  optionIds: Array<number | undefined>,
  newOptionNames: Array<string | undefined>,
  formOptions: Option[]
) => {
  const existingOptionNames = optionIds.map(
    optionId => formOptions.find(option => option.id === optionId)?.name ?? ''
  );
  const createdOptionNames = newOptionNames.filter((option): option is string => option !== undefined);

  return [...existingOptionNames, ...createdOptionNames];
};

const buildIndividualChildrenUpdatePayload = (
  sanitizedSubQuestion: SubQuestion[]
): Pick<UpdateIndividualQuestion, 'delete_children' | 'children_questions'> => {
  if (isEmpty(sanitizedSubQuestion)) {
    return { delete_children: true };
  }

  return {
    children_questions: sanitizedSubQuestion.flatMap(question => {
      const childQuestionType = getQuestionTypeLabel(question.question_type);
      if (!childQuestionType) {
        return [];
      }

      return [{
        name: question.name,
        description: question.description,
        question_type: childQuestionType as UpdateIndividualQuestion.question_type,
      }];
    }),
  };
};

type IndividualQuestionPayloadInput = {
  name: string;
  description?: string;
  questionType: string;
  options: string[];
  companyInformationRequestStatus: number;
  childrenQuestions?: SubQuestion[];
};

const buildIndividualQuestionPayload = ({
  name,
  description,
  questionType,
  options,
  companyInformationRequestStatus,
  childrenQuestions,
}: IndividualQuestionPayloadInput): UpdateIndividualQuestion => ({
  name,
  description: description ?? null,
  question_type: questionType as UpdateIndividualQuestion.question_type,
  options,
  company_information_request_status: companyInformationRequestStatus,
  ...(childrenQuestions?.length
    ? {
      children_questions: childrenQuestions.map(question => ({
        name: question.name,
        question_type: TEXT_TYPE_LABEL as UpdateIndividualQuestion.question_type,
        description: question.description,
      })),
    }
    : {}),
});

const useRequestDocsAndQuestions = (props: UseRequestDocsAndQuestionsProps) => {
  const {
    formInitialValues,
    formValues,
    isDocumentRequest,
    firmId,
    companyId,
    taskToDelete,
    setUpdateTaskList,
    setOpenDeleteDialog,
    setOpen,
    setIsEdit,
    setFormValues,
    isInitialSetup,
    primaryInformationRequestId,
    currentPageData,
  } = props;
  const [deleteRequestedQuestion, updateRequestedQuestion] = useDeleteAndUpdateRequestedQuestion();
  const [deleteFirmRequestedDocument, updateFirmRequestedDocument] = useDeleteAndUpdateFirmRequestedDocument();
  const [createRequestedQuestion] = useCreateRequestedQuestion();
  const [createFirmRequestedDocument] = useCreateFirmRequestedDocument();

  const isFirm = isNull(companyId) && !isInitialSetup;
  const taskId = isFirm ? { firm: firmId } : { company: companyId };

  const dropdownTypeValue = getQuestionTypeValue(DROPDOWN_TYPE_LABEL);
  const normalizedQuestionType = resolveQuestionTypeValue(formValues.question_type);

  const sanitizeOptions = (optionsParams: Option[]) => {
    if (dropdownTypeValue !== undefined && normalizedQuestionType !== dropdownTypeValue) {
      return { options: [], new_options: [] };
    }

    const validOptions = optionsParams.filter((option: Option) => option.name !== '');
    const capitalizedOptions = validOptions.map((option: Option) => ({ ...option, name: capitalize(option.name) }));

    const optionsWithId = capitalizedOptions.filter((option: Option) => option.id);
    const optionsWithoutId = capitalizedOptions.filter((option: Option) => !option.id);

    const optionsPayload = {
      options: optionsWithId.map((option: Option) => option.id),
      new_options: optionsWithoutId.map((option: Option) => option.name),
    };

    return optionsPayload;
  };

  const sanitizeOptionsAndSubQuestions = () => {
    const sanitizedOptions = sanitizeOptions(formValues.options);
    const filteredSubQuestions = formValues.children_questions.filter(
      (subQuestion: SubQuestion) => subQuestion.name !== ''
    );
    // We only want to allow sub-question of type text, since it's the only type of sub-question that can be created in the current frontend
    const subQuestionType = getQuestionTypeValue(TEXT_TYPE_LABEL);
    const sanitizedSubQuestion = filteredSubQuestions.map((subQuestion: SubQuestion) => ({
      ...subQuestion,
      question_type: subQuestionType,
      company: companyId,
    }));

    return {
      sanitizedOptions,
      sanitizedSubQuestion,
    };
  };

  const closeModalAndClearForm = () => {
    setOpen(false);
    setIsEdit(false);
    setFormValues(formInitialValues);
  };

  // Functions to handle requests from the Firm
  const handleUpdateTask = async () => {
    let payload: PayloadToUpdateRequestedQuestion = {
      id: formValues.id,
      name: formValues.name,
      description: formValues.description ?? '',
      folder: formValues.folder === 0 ? null : formValues.folder, // 0 is for display purposes, we need null for the backend
      reference_object_id: formValues.reference_object_id,
      reference_object_type: formValues.reference_object_type ?? null,
      company: null,
    };

    try {
      // Question
      if (!isDocumentRequest) {
        const { sanitizedSubQuestion } = sanitizeOptionsAndSubQuestions();
        // sanitizedSubQuestion is an array but with one element (the sub-question object), so we take the first element
        // We build the childrenPayload object based on whether sanitizedSubQuestion is empty or not
        // If sanitizedSubQuestion is empty (because there is no sub-question), we set delete_children to true.
        // This means that if there is no sub-question, the child question should be deleted.
        const childrenPayload = isEmpty(sanitizedSubQuestion)
          ? { delete_children: true }
          : { children_questions: sanitizedSubQuestion };

        payload = {
          ...payload,
          ...childrenPayload,
          options: toOptionNames(formValues.options),
          question_type: normalizedQuestionType,
        };

        if (isFirm) {
          payload = {
            ...payload,
            firm: firmId,
          };
          await updateRequestedQuestion(formValues.id, payload);
        } else if (isInitialSetup) {
          const questionType = getQuestionTypeLabel(formValues.question_type);
          if (!questionType) {
            return;
          }

          const individualPayload: UpdateIndividualQuestion = {
            name: formValues.name,
            description: formValues.description ?? null,
            question_type: questionType as UpdateIndividualQuestion.question_type,
            options: toOptionNames(formValues.options),
            company_information_request_status:
              primaryInformationRequestId ?? formValues.company_information_request_status ?? null,
            ...buildIndividualChildrenUpdatePayload(sanitizedSubQuestion),
          };

          await updateDestroyIndividualQuestion(formValues.id, individualPayload);
        } else {
          // belongs to the dashboard409a feature
          payload = {
            ...payload,
            company: companyId ?? null,
          };
          await retrieveUpdateDestroyCompanyQuestion(formValues.id, payload);
        }
      }

      if (isDocumentRequest) {
        if (isFirm) {
          payload = {
            ...payload,
            firm: firmId,
          };
          await updateFirmRequestedDocument(formValues.id, payload);
        } else if (isInitialSetup) {
          payload = {
            ...payload,
            company_information_request_status:
              primaryInformationRequestId ?? formValues.company_information_request_status,
          };
          // potentially we can update the appropriate document here after saving
          await updateIndividualDocument(formValues.id, payload);
        } else {
          payload = {
            ...payload,
            company: companyId ?? null,
          };
          // belongs to the dashboard409a feature
          await retrieveUpdateDestroyCompanyRequestedDocument(formValues.id, payload);
        }
      }

      closeModalAndClearForm();
      setUpdateTaskList(true);
    } catch {
      // Error notifications are handled in the service layer; keep the modal open on failure.
    }
  };

  const handleDeleteRequestedItem = async () => {
    try {
      if (isDocumentRequest && taskToDelete?.id) {
        if (isFirm) {
          await deleteFirmRequestedDocument(taskToDelete.id);
        } else if (isInitialSetup) {
          // will delete the document if no payload is provided
          await updateIndividualDocument(taskToDelete.id);
        } else {
          // belongs to the dashboard409a feature
          await retrieveUpdateDestroyCompanyRequestedDocument(taskToDelete.id);
        }
      } else if (taskToDelete?.id) {
        if (isFirm) {
          await deleteRequestedQuestion(taskToDelete.id);
        } else if (isInitialSetup) {
          await updateDestroyIndividualQuestion(taskToDelete.id);
        } else {
          // belongs to the dashboard409a feature
          await retrieveUpdateDestroyCompanyQuestion(taskToDelete.id);
        }
      }
      if (setOpenDeleteDialog) {
        setOpenDeleteDialog(false);
      }
      setUpdateTaskList(true);
    } catch {
      // Error notifications are handled in the service layer.
    }
  };

  const addOptionsAndSubQuestionsToPayload = () => {
    const { sanitizedOptions, sanitizedSubQuestion } = sanitizeOptionsAndSubQuestions();

    return {
      ...taskId,
      questions: [
        {
          ...sanitizedOptions,
          ...taskId,
          name: formValues.name,
          description: formValues.description,
          question_type: normalizedQuestionType,
          children_questions: sanitizedSubQuestion.map((question: SubQuestion) => ({
            ...question,
            question_type: normalizedQuestionType,
          })),
        },
      ],
    };
  };

  const handleRequestNewItem = async (event?: React.SyntheticEvent) => {
    event?.preventDefault();

    try {
      if (isDocumentRequest) {
        if (isFirm) {
          const payload = {
            firm: firmId,
            requested_documents: [
              {
                firm: firmId,
                name: formValues.name,
                description: formValues.description,
              },
            ],
          };
          await createFirmRequestedDocument(payload as FirmCreateDocRequestPayload);
        } else if (isInitialSetup) {
          await createIndividualDocument({
            name: formValues.name,
            company_information_request_status: primaryInformationRequestId,
            reference_object_type: currentPageData?.referenceType ?? null,
            reference_object_id: currentPageData?.referencedFeatureId ?? null,
          });
        } else {
          // belongs to the dashboard409a feature
          const payload = [
            {
              name: formValues.name,
              description: formValues.description,
              company: companyId,
            },
          ];
          await createRequestedDocuments(payload as DashboardCreateDocRequestPayload);
        }
      } else if (isFirm) {
        const payload = addOptionsAndSubQuestionsToPayload();
        await createRequestedQuestion(payload as unknown as FirmCreateQuestionRequestPayload);
      } else if (isInitialSetup) {
        if (!primaryInformationRequestId) {
          return;
        }

        const payload = addOptionsAndSubQuestionsToPayload().questions[0];
        const questionType = getQuestionTypeLabel(payload.question_type);
        if (!questionType) {
          return;
        }

        await createIndividualQuestion(
          buildIndividualQuestionPayload({
            name: formValues.name,
            description: formValues.description,
            questionType,
            options: toResolvedOptionNames(payload.options, payload.new_options, formValues.options),
            companyInformationRequestStatus: primaryInformationRequestId,
            childrenQuestions: payload.children_questions,
          })
        );
      } else {
        // belongs to the dashboard409a feature
        const payload = addOptionsAndSubQuestionsToPayload();
        await createQuestions(payload as unknown as DashboardCreateQuestionRequestPayload);
      }

      closeModalAndClearForm();
      setUpdateTaskList(true);
    } catch {
      // Error notifications are handled in the service layer; keep the modal open on failure.
    }
  };

  const handleCancel = () => {
    closeModalAndClearForm();
  };

  return {
    handleUpdateTask,
    handleRequestNewItem,
    handleDeleteRequestedItem,
    handleCancel,
  };
};

export default useRequestDocsAndQuestions;
